import type { Product } from '@/types/product.types';
import type { ZoneStockage } from '@/types/inventory.types';

/**
 * Résultat du parsing d'un élément vocal
 */
export interface ParsedVoiceItem {
  id: string;
  rawText: string;
  quantity: number;
  unit: string;
  productName: string;
  matchedProduct: Product | null;
  matchScore: number;
  suggestedProducts: Product[];
  zone: ZoneStockage | null;
  status: 'matched' | 'ambiguous' | 'not_found';
  dlc: string | null;
  numeroLot: string | null;
}

/**
 * Mapping des mots français vers les nombres
 */
const FRENCH_NUMBERS: Record<string, number> = {
  'un': 1, 'une': 1,
  'deux': 2,
  'trois': 3,
  'quatre': 4,
  'cinq': 5,
  'six': 6,
  'sept': 7,
  'huit': 8,
  'neuf': 9,
  'dix': 10,
  'onze': 11,
  'douze': 12,
  'treize': 13,
  'quatorze': 14,
  'quinze': 15,
  'seize': 16,
  'vingt': 20,
  'trente': 30,
  'quarante': 40,
  'cinquante': 50,
  'soixante': 60,
  'cent': 100,
  'demi': 0.5,
  'demie': 0.5,
};

/**
 * Mapping des unités parlées vers les unités système
 */
const UNIT_MAPPINGS: Record<string, string> = {
  // Poids
  'kilo': 'kg',
  'kilos': 'kg',
  'kilogramme': 'kg',
  'kilogrammes': 'kg',
  'kg': 'kg',
  'gramme': 'g',
  'grammes': 'g',
  'g': 'g',
  // Volume
  'litre': 'L',
  'litres': 'L',
  'l': 'L',
  'millilitre': 'mL',
  'millilitres': 'mL',
  'ml': 'mL',
  'centilitre': 'mL',
  'centilitres': 'mL',
  'cl': 'mL',
  // Unités
  'unité': 'unité',
  'unités': 'unité',
  'pièce': 'unité',
  'pièces': 'unité',
  'piece': 'unité',
  'pieces': 'unité',
  'bouteille': 'unité',
  'bouteilles': 'unité',
  'boîte': 'unité',
  'boîtes': 'unité',
  'boite': 'unité',
  'boites': 'unité',
  'paquet': 'unité',
  'paquets': 'unité',
  'sachet': 'unité',
  'sachets': 'unité',
  'barquette': 'unité',
  'barquettes': 'unité',
  'pot': 'unité',
  'pots': 'unité',
  'pack': 'unité',
  'packs': 'unité',
  'carton': 'unité',
  'cartons': 'unité',
  'caisse': 'unité',
  'caisses': 'unité',
  'palette': 'unité',
  'palettes': 'unité',
  'sac': 'unité',
  'sacs': 'unité',
  'bac': 'unité',
  'bacs': 'unité',
  'tube': 'unité',
  'tubes': 'unité',
  'bocal': 'unité',
  'bocaux': 'unité',
};

/**
 * Séparateurs pour diviser la phrase en éléments
 */
const SEPARATORS = /[,;]|\bet\b|\bplus\b|\bensuite\b|\baussi\b/gi;

/**
 * Mots-clés pour détecter une DLC dans la commande vocale
 */
const DLC_KEYWORDS = ['dlc', 'date limite', 'péremption', 'peremption', 'expire', 'expiration', 'jusqu\'au', 'jusquau', 'valable jusqu'];

/**
 * Mapping des mois français vers les numéros
 */
const FRENCH_MONTHS: Record<string, number> = {
  'janvier': 1, 'jan': 1,
  'février': 2, 'fevrier': 2, 'fev': 2, 'fév': 2,
  'mars': 3, 'mar': 3,
  'avril': 4, 'avr': 4,
  'mai': 5,
  'juin': 6, 'jun': 6,
  'juillet': 7, 'juil': 7, 'jul': 7,
  'août': 8, 'aout': 8, 'aou': 8,
  'septembre': 9, 'sept': 9, 'sep': 9,
  'octobre': 10, 'oct': 10,
  'novembre': 11, 'nov': 11,
  'décembre': 12, 'decembre': 12, 'dec': 12, 'déc': 12,
};

/**
 * Service de parsing des commandes vocales
 */
class VoiceParserService {
  /**
   * Parse une transcription vocale en liste de produits
   */
  parseTranscript(transcript: string, products: Product[]): ParsedVoiceItem[] {
    if (!transcript.trim()) {
      return [];
    }

    // Normaliser le texte
    const normalizedText = this.normalizeText(transcript);

    // Diviser en éléments individuels
    const segments = normalizedText.split(SEPARATORS).filter((s) => s.trim());

    // Parser chaque segment
    const parsedItems: ParsedVoiceItem[] = [];

    for (const segment of segments) {
      const trimmed = segment.trim();
      if (trimmed) {
        const parsed = this.parseSegment(trimmed, products);
        if (parsed) {
          parsedItems.push(parsed);
        }
      }
    }

    return parsedItems;
  }

  /**
   * Parse un segment individuel (ex: "10 kilos de tomates dlc 15 février")
   */
  private parseSegment(segment: string, products: Product[]): ParsedVoiceItem | null {
    // D'abord, extraire la DLC si présente
    const { cleanedSegment, dlc } = this.extractDLC(segment);

    const words = cleanedSegment.toLowerCase().split(/\s+/);

    let quantity = 1;
    let unit = 'unité';
    let productNameWords: string[] = [];
    let quantityFound = false;
    let unitFound = false;

    for (const word of words) {
      if (!word) continue;

      // Chercher un nombre
      if (!quantityFound) {
        // Nombre numérique
        const numMatch = word.match(/^(\d+(?:[.,]\d+)?)$/);
        if (numMatch && numMatch[1]) {
          quantity = parseFloat(numMatch[1].replace(',', '.'));
          quantityFound = true;
          continue;
        }

        // Nombre en lettres
        const frenchNum = FRENCH_NUMBERS[word];
        if (frenchNum !== undefined) {
          quantity = frenchNum;
          quantityFound = true;
          continue;
        }

        // Combinaisons comme "vingt-cinq"
        const compoundMatch = word.match(/^(\w+)-(\w+)$/);
        if (compoundMatch) {
          const first = compoundMatch[1];
          const second = compoundMatch[2];
          if (first && second) {
            const firstNum = FRENCH_NUMBERS[first];
            const secondNum = FRENCH_NUMBERS[second];
            if (firstNum !== undefined && secondNum !== undefined) {
              quantity = firstNum + secondNum;
              quantityFound = true;
              continue;
            }
          }
        }
      }

      // Chercher une unité
      const mappedUnit = UNIT_MAPPINGS[word];
      if (!unitFound && mappedUnit) {
        unit = mappedUnit;
        unitFound = true;
        continue;
      }

      // Ignorer les mots de liaison
      if (['de', 'du', 'd', 'des', 'le', 'la', 'les', 'l'].includes(word)) {
        continue;
      }

      // Le reste est le nom du produit
      productNameWords.push(word);
    }

    const productName = productNameWords.join(' ').trim();

    if (!productName) {
      return null;
    }

    // Matcher avec les produits existants
    const { matchedProduct, matchScore, suggestedProducts } = this.findMatchingProduct(
      productName,
      products
    );

    // Déterminer la zone en fonction de la catégorie du produit
    const zone = matchedProduct ? this.inferZoneFromProduct(matchedProduct) : null;

    // Déterminer le statut
    let status: ParsedVoiceItem['status'] = 'not_found';
    if (matchScore >= 0.8) {
      status = 'matched';
    } else if (matchScore >= 0.5 || suggestedProducts.length > 0) {
      status = 'ambiguous';
    }

    return {
      id: crypto.randomUUID(),
      rawText: segment,
      quantity,
      unit,
      productName,
      matchedProduct,
      matchScore,
      suggestedProducts,
      zone,
      status,
      dlc,
      numeroLot: null,
    };
  }

  /**
   * Extrait la DLC d'un segment vocal
   * Formats supportés:
   * - "dlc 15 février" / "dlc le 15 février"
   * - "dlc 15/02" / "dlc 15/02/2025"
   * - "expire le 15 février"
   * - "péremption 15 février"
   * - "dans X jours"
   */
  private extractDLC(segment: string): { cleanedSegment: string; dlc: string | null } {
    const lowerSegment = segment.toLowerCase();

    // Chercher si un mot-clé DLC est présent
    let dlcKeywordIndex = -1;
    let dlcKeywordLength = 0;

    for (const keyword of DLC_KEYWORDS) {
      const index = lowerSegment.indexOf(keyword);
      if (index !== -1) {
        dlcKeywordIndex = index;
        dlcKeywordLength = keyword.length;
        break;
      }
    }

    if (dlcKeywordIndex === -1) {
      return { cleanedSegment: segment, dlc: null };
    }

    // Extraire la partie après le mot-clé DLC
    const beforeDLC = segment.substring(0, dlcKeywordIndex).trim();
    const afterDLC = segment.substring(dlcKeywordIndex + dlcKeywordLength).trim();

    // Parser la date
    const dlc = this.parseDateFromText(afterDLC);

    return {
      cleanedSegment: beforeDLC,
      dlc,
    };
  }

  /**
   * Parse une date à partir d'un texte
   */
  private parseDateFromText(text: string): string | null {
    const cleanText = text.toLowerCase().replace(/^(le\s+)?/, '').trim();

    // Format "dans X jours"
    const daysMatch = cleanText.match(/dans\s+(\d+)\s+jours?/);
    if (daysMatch && daysMatch[1]) {
      const days = parseInt(daysMatch[1]);
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    }

    // Format numérique: 15/02 ou 15/02/2025 ou 15-02-2025
    const numericMatch = cleanText.match(/(\d{1,2})[\/\-](\d{1,2})(?:[\/\-](\d{2,4}))?/);
    if (numericMatch && numericMatch[1] && numericMatch[2]) {
      const day = parseInt(numericMatch[1]);
      const month = parseInt(numericMatch[2]);
      let year = numericMatch[3] ? parseInt(numericMatch[3]) : new Date().getFullYear();

      // Si année sur 2 chiffres, ajouter 2000
      if (year < 100) {
        year += 2000;
      }

      // Si la date est passée cette année, prendre l'année prochaine
      const date = new Date(year, month - 1, day);
      if (date < new Date() && !numericMatch[3]) {
        date.setFullYear(date.getFullYear() + 1);
      }

      return date.toISOString().split('T')[0];
    }

    // Format textuel: "15 février" ou "15 fév" ou "15 février 2025"
    const textMatch = cleanText.match(/(\d{1,2})\s+([a-zéûôàèùâêîôûë]+)(?:\s+(\d{4}))?/);
    if (textMatch && textMatch[1] && textMatch[2]) {
      const day = parseInt(textMatch[1]);
      const monthStr = textMatch[2];
      const month = FRENCH_MONTHS[monthStr];

      if (month) {
        let year = textMatch[3] ? parseInt(textMatch[3]) : new Date().getFullYear();

        // Si la date est passée cette année, prendre l'année prochaine
        const date = new Date(year, month - 1, day);
        if (date < new Date() && !textMatch[3]) {
          date.setFullYear(date.getFullYear() + 1);
        }

        return date.toISOString().split('T')[0];
      }
    }

    return null;
  }

  /**
   * Trouve le produit correspondant au nom dicté
   */
  private findMatchingProduct(
    productName: string,
    products: Product[]
  ): {
    matchedProduct: Product | null;
    matchScore: number;
    suggestedProducts: Product[];
  } {
    if (!products.length) {
      return { matchedProduct: null, matchScore: 0, suggestedProducts: [] };
    }

    const normalizedSearch = this.normalizeText(productName);

    // Calculer les scores de similarité
    const scored = products.map((product) => {
      const normalizedName = this.normalizeText(product.nom);
      const score = this.calculateSimilarity(normalizedSearch, normalizedName);
      return { product, score };
    });

    // Trier par score décroissant
    scored.sort((a, b) => b.score - a.score);

    const bestMatch = scored[0];
    if (!bestMatch) {
      return { matchedProduct: null, matchScore: 0, suggestedProducts: [] };
    }

    const suggestions = scored.slice(0, 5).filter((s) => s.score >= 0.3);

    return {
      matchedProduct: bestMatch.score >= 0.5 ? bestMatch.product : null,
      matchScore: bestMatch.score,
      suggestedProducts: suggestions.map((s) => s.product),
    };
  }

  /**
   * Calcule la similarité entre deux chaînes (0-1)
   */
  private calculateSimilarity(str1: string, str2: string): number {
    // Match exact
    if (str1 === str2) return 1;

    // L'un contient l'autre
    if (str2.includes(str1)) return 0.9;
    if (str1.includes(str2)) return 0.85;

    // Distance de Levenshtein normalisée
    const maxLen = Math.max(str1.length, str2.length);
    if (maxLen === 0) return 1;

    const distance = this.levenshteinDistance(str1, str2);
    const similarity = 1 - distance / maxLen;

    // Bonus si les premiers caractères correspondent
    const prefixMatch = this.commonPrefixLength(str1, str2);
    const prefixBonus = prefixMatch / maxLen * 0.2;

    return Math.min(1, similarity + prefixBonus);
  }

  /**
   * Distance de Levenshtein entre deux chaînes
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const m = str1.length;
    const n = str2.length;

    if (m === 0) return n;
    if (n === 0) return m;

    // Utiliser un tableau 1D pour optimiser la mémoire et éviter les problèmes de typage
    let prevRow: number[] = Array.from({ length: n + 1 }, (_, j) => j);
    let currRow: number[] = new Array(n + 1).fill(0);

    for (let i = 1; i <= m; i++) {
      currRow[0] = i;
      for (let j = 1; j <= n; j++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        currRow[j] = Math.min(
          (prevRow[j] ?? 0) + 1,
          (currRow[j - 1] ?? 0) + 1,
          (prevRow[j - 1] ?? 0) + cost
        );
      }
      [prevRow, currRow] = [currRow, prevRow];
    }

    return prevRow[n] ?? 0;
  }

  /**
   * Longueur du préfixe commun
   */
  private commonPrefixLength(str1: string, str2: string): number {
    let i = 0;
    while (i < str1.length && i < str2.length && str1[i] === str2[i]) {
      i++;
    }
    return i;
  }

  /**
   * Normalise le texte (minuscules, sans accents)
   */
  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  /**
   * Infère la zone de stockage en fonction de la catégorie du produit
   */
  private inferZoneFromProduct(product: Product): ZoneStockage {
    const category = product.categorie.toLowerCase();

    // Zone négative (surgelés)
    if (category.includes('surgel')) {
      return 'negative';
    }

    // Frigo
    if (
      category.includes('laitag') ||
      category.includes('viande') ||
      category.includes('poisson') ||
      category.includes('légume') ||
      category.includes('fruit')
    ) {
      return 'frigo';
    }

    // Cave (boissons)
    if (
      category.includes('vin') ||
      category.includes('alcool') ||
      category.includes('bière') ||
      category.includes('boisson')
    ) {
      return 'cave';
    }

    // Entretien
    if (
      category.includes('entretien') ||
      category.includes('hygiène') ||
      category.includes('emballage') ||
      category.includes('jetable')
    ) {
      return 'entretien';
    }

    // Réserve par défaut pour les produits secs
    if (
      category.includes('conserve') ||
      category.includes('sec') ||
      category.includes('huile') ||
      category.includes('pâte') ||
      category.includes('riz') ||
      category.includes('farine') ||
      category.includes('épice') ||
      category.includes('condiment')
    ) {
      return 'reserve';
    }

    // Zone positive par défaut
    return 'positive';
  }
}

// Export d'une instance unique
export const voiceParserService = new VoiceParserService();
